import { startAuthentication } from '@simplewebauthn/browser';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/atoms/Button';
import ErrorMessage from '@/components/atoms/ErrorMessage';
import { useAuthSuccess } from '@/hooks/useAuthSuccess';
import {
    useLazyMeQuery,
    useVerifyWebauthnMutation,
    useVerifyWebauthnOptionsMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import { getErrorCode, stringifyError } from '@/utils/errors';

import { Actions, Subtitle, Title } from '../Mfa.style';

type Props = {
    mfaToken: string;
};

const WebAuthnTab = ({ mfaToken }: Props): ReactElement => {
    const [getOptions, { error: optionsError, isLoading: optionsLoading }] = useVerifyWebauthnOptionsMutation();
    const [verify, { data: verifyData, error: verifyError, isLoading: verifyLoading }] = useVerifyWebauthnMutation();
    const [getMe, { data: meData, error: meError }] = useLazyMeQuery();
    const { handleAuthSuccess, error: authSuccessError } = useAuthSuccess();

    const [ceremonyError, setCeremonyError] = useState<string | null>(null);
    const [ceremonyRunning, setCeremonyRunning] = useState(false);
    const [bearerToken, setBearerToken] = useState<string | null>(null);

    // Run the full ceremony from the click handler so every attempt fetches fresh options
    // and re-opens the OS prompt. Driving this from a useEffect with `optionsData` as a
    // dep silently breaks retry-after-cancel: the cached options reference doesn't change
    // and the ceremony never re-fires.
    const handleAuthenticate = useCallback(async (): Promise<void> => {
        if (ceremonyRunning) return;
        setCeremonyError(null);
        setCeremonyRunning(true);
        try {
            const result = await getOptions({ token: mfaToken }).unwrap();
            const assertion = await startAuthentication({ optionsJSON: result.options });
            verify({ token: mfaToken, assertion });
        } catch (err) {
            setCeremonyError(err instanceof Error ? err.message : 'webauthn_ceremony_failed');
        } finally {
            setCeremonyRunning(false);
        }
    }, [ceremonyRunning, mfaToken, getOptions, verify]);

    useEffect(() => {
        if (!verifyData?.accessToken) return;
        // 3rd-party redirect flow — same contract as TOTP verify.
        if (verifyData.redirectUri) {
            const separator = verifyData.redirectUri.includes('?') ? '&' : '?';
            window.location.assign(`${verifyData.redirectUri}${separator}bearer=${verifyData.accessToken}`);
            return;
        }
        setBearerToken(verifyData.accessToken);
        getMe({ token: verifyData.accessToken });
    }, [verifyData, getMe]);

    useEffect(() => {
        if (meData && bearerToken) {
            handleAuthSuccess(meData, bearerToken);
        }
    }, [meData, bearerToken, handleAuthSuccess]);

    const verifyErrorCode = verifyError ? getErrorCode(verifyError) : null;
    const optionsErrorCode = optionsError ? getErrorCode(optionsError) : null;
    const errorLabel =
        verifyErrorCode === 'webauthn_verification_failed' ||
        verifyErrorCode === 'webauthn_credential_not_found' ||
        verifyErrorCode === 'webauthn_counter_regression' ? (
            <FormattedMessage id="mfa.verify.webauthn.failed" />
        ) : verifyErrorCode === 'mfa_token_expired' || verifyErrorCode === 'mfa_token_required' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : optionsErrorCode === 'webauthn_no_credentials' ? (
            <FormattedMessage id="mfa.verify.webauthn.noCredentials" />
        ) : optionsErrorCode === 'mfa_token_expired' || optionsErrorCode === 'mfa_token_required' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : authSuccessError === 'no_brands_configured' ? (
            <FormattedMessage id="login.noBrandsConfigured" />
        ) : authSuccessError === 'guest_brand_login_failed' ? (
            <FormattedMessage id="login.guestBrandLoginFailed" />
        ) : ceremonyError ? (
            <FormattedMessage id="mfa.verify.webauthn.ceremonyFailed" />
        ) : (
            stringifyError(optionsError) || stringifyError(verifyError) || stringifyError(meError)
        );

    const inFlight = optionsLoading || verifyLoading || ceremonyRunning;

    return (
        <>
            <Title>
                <FormattedMessage id="mfa.verify.webauthn.title" />
            </Title>
            <Subtitle>
                <FormattedMessage id="mfa.verify.webauthn.subtitle" />
            </Subtitle>
            {errorLabel ? <ErrorMessage>{errorLabel}</ErrorMessage> : null}
            <Actions>
                <Button buttonType="primary" fullWidth thick disabled={inFlight} onClick={handleAuthenticate}>
                    <FormattedMessage id="mfa.verify.webauthn.authenticate" />
                </Button>
            </Actions>
        </>
    );
};

export default WebAuthnTab;
